import React, { useState, useCallback, useMemo, useEffect, forwardRef, useImperativeHandle } from 'react';
import { Button } from '@components/common/Button';
import { ExpressionNumberInput } from '@components/common/ExpressionNumberInput';
import { SearchableSelect } from '@components/common/SearchableSelect';
import { ReferenceDropdown } from './ReferenceDropdown';
import { LineItemsEditor } from './LineItemsEditor';
import { TaxItemsEditor } from './TaxItemsEditor';
import type { DocumentFieldsResponse, ExtractionStatusResponse, TaxIdInput } from '@schemas/api';
import { TAX_ID_TYPE_OPTIONS, normalizeTaxIdEntries } from '@/lib/vendorTaxIds';
import type { DocumentFieldOption } from '@/types/onboarding';
import { Plus } from 'lucide-react';

interface EditableDocumentFieldsProps {
  documentData: DocumentFieldsResponse;
  isLoading?: boolean;
  perFieldConf?: Record<string, number>;
  confidenceThreshold?: number;
  excludeLineItems?: boolean;
  documentType?: 'purchase_order' | 'receipt' | 'invoice';
  selectedFields?: string[];
  fieldCatalog?: DocumentFieldOption[];
  taxIdTypes?: NonNullable<ExtractionStatusResponse['tax_id_types']>;
  onMatchingRelevantChange?: (hasChanges: boolean) => void;
  erpConnectionId?: string;
}

export interface EditableDocumentFieldsHandle {
  getEditedData: () => Record<string, any>;
  hasChanges: () => boolean;
}

type FieldType = 'text' | 'number' | 'date' | 'vendor' | "purchase_order" | 'array' | 'tax_ids' | 'tax_items';

interface EditableField {
  key: string;
  type: FieldType;
  label: string;
  value: any;
  originalValue: any;
}

interface TaxIdRow {
  id: string;
  key: string;
  value: string;
}

const PRIORITIZED_TAX_ID_KEYS = ['CA_GST', 'CA_QST'] as const;

const TAX_ID_OPTIONS = [
  ...TAX_ID_TYPE_OPTIONS.filter((option) => option.value === 'CA_GST'),
  ...TAX_ID_TYPE_OPTIONS.filter((option) => option.value === 'CA_QST'),
  ...TAX_ID_TYPE_OPTIONS.filter((option) => !(PRIORITIZED_TAX_ID_KEYS as readonly string[]).includes(option.value)),
];

const CONFIG_TO_FIELD_KEY: Record<string, string> = {
  vendor: 'vendor_id',
  vendor_name: 'vendor_id',
  vendor_gst_number: 'tax_ids',
  vendor_qst_number: 'tax_ids',
  invoice_number: 'external_invoice_number',
  receipt_number: 'external_receipt_number',
  po_numbers: 'po_number',
  total_amount: 'total',
  tax: 'tax_total',
  discount: 'discount_total',
  receipt_date: 'document_date',
  line_items: 'lines',
};

function mapConfigKeyToFieldKey(configKey: string): string {
  return CONFIG_TO_FIELD_KEY[configKey] ?? configKey;
}

function normalizeTaxIds(taxIds?: TaxIdInput): DocumentFieldsResponse['tax_ids'] {
  const entries = normalizeTaxIdEntries(taxIds)
    .sort((left, right) => left.type.localeCompare(right.type));
  return entries.length > 0 ? entries : null;
}

function buildTaxIdRows(taxIds?: TaxIdInput): TaxIdRow[] {
  const normalizedTaxIds = normalizeTaxIds(taxIds);

  if (!normalizedTaxIds) {
    return [];
  }

  return normalizedTaxIds.map((entry, index) => ({
    id: `tax-id-${index}-${entry.type}`,
    key: entry.type,
    value: entry.value,
  }));
}

function buildTaxIdsFromRows(rows: TaxIdRow[]): DocumentFieldsResponse['tax_ids'] {
  return normalizeTaxIds(rows.map((row) => ({ type: row.key, value: row.value })));
}

function normalizeTaxItems(
  taxItems?: DocumentFieldsResponse['tax_items']
): DocumentFieldsResponse['tax_items'] {
  if (!Array.isArray(taxItems)) {
    return null;
  }

  const normalizedItems = taxItems
    .filter((item): item is NonNullable<DocumentFieldsResponse['tax_items']>[number] => Boolean(item && typeof item === 'object'))
    .map((item) => {
      const normalizedItem: Record<string, any> = {};

      Object.entries(item).forEach(([key, value]) => {
        if (typeof value === 'string') {
          const trimmedValue = value.trim();
          if (trimmedValue) {
            normalizedItem[key] = trimmedValue;
          }
          return;
        }

        if (value !== null && value !== undefined) {
          normalizedItem[key] = value;
        }
      });

      return normalizedItem;
    })
    .filter((item) => Object.keys(item).length > 0);

  return normalizedItems.length > 0 ? normalizedItems : null;
}

function buildEditedDocumentData(documentData: DocumentFieldsResponse): Record<string, any> {
  return {
    vendor_id: documentData.vendor_id,
    vendor_name: documentData.vendor_name,
    vendor_name_extracted: documentData.vendor_name_extracted,
    external_invoice_number: documentData.external_invoice_number,
    external_receipt_number: documentData.external_receipt_number,
    po_number: documentData.po_number,
    subtotal: documentData.subtotal,
    total: documentData.total,
    discount_total: documentData.discount_total,
    additional_charges: documentData.additional_charges,
    tax_total: documentData.tax_total,
    tax_items: normalizeTaxItems(documentData.tax_items),
    currency: documentData.currency,
    invoice_date: documentData.invoice_date,
    receipt_date: documentData.receipt_date,
    document_date: documentData.document_date,
    due_date: documentData.due_date,
    payment_terms: documentData.payment_terms,
    tax_ids: normalizeTaxIds(documentData.tax_ids),
    lines: documentData.lines,
  };
}

export const EditableDocumentFields = forwardRef<EditableDocumentFieldsHandle, EditableDocumentFieldsProps>(
  ({ documentData, isLoading = false, perFieldConf = {}, confidenceThreshold = 0.9, excludeLineItems = false, documentType = 'invoice', selectedFields, fieldCatalog = [], taxIdTypes = [], onMatchingRelevantChange, erpConnectionId }, ref) => {
    const baselineData = useMemo(() => buildEditedDocumentData(documentData), [documentData]);
    const [editedData, setEditedData] = useState<Record<string, any>>(() => baselineData);
    const [taxIdRows, setTaxIdRows] = useState<TaxIdRow[]>(() => buildTaxIdRows(documentData.tax_ids));
    const [manuallyShownFieldKeys, setManuallyShownFieldKeys] = useState<string[]>([]);
    const [fieldToAdd, setFieldToAdd] = useState('');
    const taxIdTypeOptions = useMemo(() => {
      const options = new Map<string, string>(
        TAX_ID_OPTIONS.map((option) => [option.value, option.label])
      );

      taxIdRows.forEach((row) => {
        if (row.key && !options.has(row.key)) {
          options.set(row.key, row.key);
        }
      });

      return Array.from(options, ([value, label]) => ({ value, label }));
    }, [taxIdRows]);

    useEffect(() => {
      setEditedData(baselineData);
      setTaxIdRows(buildTaxIdRows(documentData.tax_ids));
    }, [baselineData, documentData.tax_ids]);

    useEffect(() => {
      setManuallyShownFieldKeys([]);
      setFieldToAdd('');
    }, [documentData.id, documentType]);

    useEffect(() => {
      if (!onMatchingRelevantChange) {
        return;
      }
      const matchingKeys = [
        'vendor_id',
        'vendor_name_extracted',
        'po_number',
        'currency',
        'subtotal',
        'total',
        'tax_total',
        'lines',
      ];
      const hasRelevantChanges = matchingKeys.some(
        (key) => JSON.stringify(editedData[key]) !== JSON.stringify(baselineData[key])
      );
      onMatchingRelevantChange(hasRelevantChanges);
    }, [baselineData, editedData, onMatchingRelevantChange]);

    // Expose methods for parent components to access edited data
    useImperativeHandle(
      ref,
      () => ({
        getEditedData: () => editedData,
        hasChanges: () => JSON.stringify(editedData) !== JSON.stringify(baselineData),
      }),
      [baselineData, editedData]
    );

    // Detect field type based on key
    const detectFieldType = useCallback((key: string): FieldType => {
      if (key === 'tax_ids') return 'tax_ids';
      if (key === 'tax_items') return 'tax_items';
      if (key === 'po_number' && documentType !== 'purchase_order') return 'purchase_order';

      if (key === 'po_number' || key === 'receipt_number' || key === 'invoice_number' ||
          key === 'external_invoice_number' || key === 'external_receipt_number') {
        return 'text';
      }

      if (key === 'vendor_name' || key === 'vendor_id') return 'vendor';
      if (key === 'lines') return 'array';

      const lowerKey = key.toLowerCase();
      if (lowerKey.includes('date')) return 'date';
      if (lowerKey.includes('qty') || lowerKey.includes('quantity') ||
          lowerKey.includes('price') || lowerKey.includes('total') ||
          lowerKey.includes('amount') || lowerKey.includes('tax') ||
          lowerKey.includes('subtotal') || lowerKey.includes('charges') ||
          lowerKey.includes('discount')) {
        return 'number';
      }

      return 'text';
    }, [documentType]);

    const enabledFieldKeys = useMemo((): Set<string> | null => {
      if (!selectedFields) {
        return null;
      }

      const enabled = new Set<string>();
      selectedFields.forEach((configKey) => {
        if (configKey.startsWith('line_items.')) return;
        const componentKey = mapConfigKeyToFieldKey(configKey);
        enabled.add(componentKey);
      });
      manuallyShownFieldKeys.forEach((fieldKey) => enabled.add(fieldKey));
      return enabled;
    }, [manuallyShownFieldKeys, selectedFields]);
    const manuallyShownFieldKeySet = useMemo(() => new Set(manuallyShownFieldKeys), [manuallyShownFieldKeys]);

    const fieldLabelByKey = useMemo(() => {
      const labels = new Map<string, string>();
      fieldCatalog.forEach((option) => {
        if (option.key.startsWith('line_items.')) return;
        labels.set(mapConfigKeyToFieldKey(option.key), option.label);
      });
      return labels;
    }, [fieldCatalog]);

    // Parse document fields
    const fields = useMemo(() => {
      const parsed: EditableField[] = [];
      const fieldOrder = [
        'external_invoice_number',
        'external_receipt_number',
        'vendor_id',  // Single vendor field that shows name and allows changing via dropdown
        'invoice_date',
        'receipt_date',
        'document_date',
        'due_date',
        'payment_terms',
        'currency',
        'subtotal',
        'discount_total',
        'additional_charges',
        'tax_total',
        'total',
        'tax_items',
        'tax_ids',
        'lines',
      ];
      fieldOrder.splice(2, 0, 'po_number');
      if (documentType !== 'invoice') {
        const subtotalIndex = fieldOrder.indexOf('subtotal');
        if (subtotalIndex >= 0) {
          fieldOrder.splice(subtotalIndex, 1);
        }
      }

      const orderedFieldKeys = [...fieldOrder];
      if (enabledFieldKeys) {
        enabledFieldKeys.forEach((key) => {
          if (!orderedFieldKeys.includes(key)) {
            orderedFieldKeys.push(key);
          }
        });
      }

      orderedFieldKeys.forEach((key) => {
        if (key === 'tax_ids' && documentType !== 'invoice') {
          return;
        }

        // Filter by selected fields when config is provided
        if (enabledFieldKeys !== null && !enabledFieldKeys.has(key)) return;

        const value = editedData[key];

        // Skip null/undefined fields only when there is no active config filter.
        // When a config is present, always render configured fields (even if null/undefined)
        // so reviewers can manually enter values the AI didn't extract.
        if ((value === null || value === undefined) && enabledFieldKeys === null && !manuallyShownFieldKeySet.has(key)) {
          return;
        }

        if (key === 'lines') {
          parsed.push({
            key,
            type: 'array',
            label: 'Line Items',
            value: Array.isArray(value) ? value : [],
            originalValue: documentData.lines || [],
          });
        } else if (key === 'tax_items') {
          parsed.push({
            key,
            type: 'tax_items',
            label: 'Tax Lines',
            value: Array.isArray(value) ? value : [],
            originalValue: normalizeTaxItems(documentData.tax_items),
          });
        } else {
          const type = detectFieldType(key);
          const label = key === 'vendor_id'
            ? 'Vendor Name'
            : key === 'tax_ids'
            ? 'Document Tax IDs'
            : fieldLabelByKey.get(key) ?? key.replace(/_/g, ' ').split(' ').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
          parsed.push({
            key,
            type,
            label,
            value: value ?? '',
            originalValue: documentData[key as keyof DocumentFieldsResponse] ?? '',
          });
        }
      });

      return parsed;
    }, [documentType, editedData, detectFieldType, documentData, enabledFieldKeys, fieldLabelByKey, manuallyShownFieldKeySet]);

    const renderedFieldKeys = useMemo(() => new Set(fields.map((field) => field.key)), [fields]);
    const addableFieldOptions = useMemo(() => {
      const seen = new Set<string>();

      return fieldCatalog
        .filter((option) => !option.key.startsWith('line_items.'))
        .map((option) => ({
          ...option,
          fieldKey: mapConfigKeyToFieldKey(option.key),
        }))
        .filter((option) => {
          if (seen.has(option.fieldKey) || renderedFieldKeys.has(option.fieldKey)) {
            return false;
          }
          seen.add(option.fieldKey);
          return true;
        });
    }, [fieldCatalog, renderedFieldKeys]);

    const handleFieldChange = useCallback((key: string, newValue: any) => {
      setEditedData((prev) => ({
        ...prev,
        [key]: newValue,
      }));
    }, []);

    const handleLineItemsChange = useCallback((newItems: any[]) => {
      setEditedData((prev) => ({
        ...prev,
        lines: newItems,
      }));
    }, []);

    const handleTaxItemsChange = useCallback((newItems: NonNullable<DocumentFieldsResponse['tax_items']>) => {
      setEditedData((prev) => ({
        ...prev,
        tax_items: newItems,
      }));
    }, []);

    const handleTaxIdRowsChange = useCallback((updater: (rows: TaxIdRow[]) => TaxIdRow[]) => {
      setTaxIdRows((prev) => {
        const next = updater(prev);
        handleFieldChange('tax_ids', buildTaxIdsFromRows(next));
        return next;
      });
    }, [handleFieldChange]);

    const handleTaxIdRowChange = useCallback((rowId: string, field: 'key' | 'value', newValue: string) => {
      handleTaxIdRowsChange((rows) => rows.map((row) => (
        row.id === rowId
          ? { ...row, [field]: newValue }
          : row
      )));
    }, [handleTaxIdRowsChange]);

    const handleAddTaxIdRow = useCallback(() => {
      handleTaxIdRowsChange((rows) => [
        ...rows,
        {
          id: `tax-id-${Date.now()}`,
          key: 'CA_GST',
          value: '',
        },
      ]);
    }, [handleTaxIdRowsChange]);

    const handleRemoveTaxIdRow = useCallback((rowId: string) => {
      handleTaxIdRowsChange((rows) => rows.filter((row) => row.id !== rowId));
    }, [handleTaxIdRowsChange]);

    const handleAddField = useCallback(() => {
      if (!fieldToAdd) {
        return;
      }

      setManuallyShownFieldKeys((prev) => (
        prev.includes(fieldToAdd) ? prev : [...prev, fieldToAdd]
      ));
      setFieldToAdd('');
    }, [fieldToAdd]);

    return (
      <div className="space-y-4">
        {addableFieldOptions.length > 0 && (
          <div className="flex flex-wrap items-center gap-2 rounded-lg border border-dashed border-gray-300 bg-white p-3 dark:border-gray-600 dark:bg-gray-900">
            <SearchableSelect
              value={fieldToAdd}
              onChange={setFieldToAdd}
              disabled={isLoading}
              className="min-w-[180px] flex-1"
              options={[
                { id: '', label: 'Add missed field...' },
                ...addableFieldOptions.map((option) => ({ id: option.fieldKey, label: option.label })),
              ]}
              placeholder="Add missed field..."
              clearable={false}
            />
            <Button
              type="button"
              size="sm"
              variant="secondary"
              disabled={!fieldToAdd || isLoading}
              onClick={handleAddField}
              className="inline-flex items-center gap-2"
            >
              <Plus className="h-4 w-4" />
              Add Field
            </Button>
          </div>
        )}
        <div className="space-y-3">
          {fields.filter(f => !(excludeLineItems && f.type === 'array')).map((field) => {
            const fieldConfidence = perFieldConf[field.key];
            const isLowConfidence = fieldConfidence !== undefined && fieldConfidence < confidenceThreshold;

            return (
              <div
                key={field.key}
                className={`border rounded-lg p-4 space-y-2 ${
                  isLowConfidence
                    ? 'bg-yellow-50 dark:bg-yellow-900 border-yellow-300 dark:border-yellow-700'
                    : 'bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700'
                }`}
              >
                <div className="flex items-start justify-between">
                  <label className="block text-sm font-medium text-gray-900 dark:text-gray-100">
                    {field.label}
                  </label>
                  {fieldConfidence !== undefined && (
                    <div className="flex items-center gap-2">
                      <div className="text-xs font-semibold">
                        {isLowConfidence ? (
                          <span className="text-yellow-700 dark:text-yellow-300 bg-yellow-100 dark:bg-yellow-900 px-2 py-1 rounded">
                            Low Confidence: {(fieldConfidence * 100).toFixed(0)}%
                          </span>
                        ) : (
                          <span className="text-green-700 dark:text-green-300 bg-green-100 dark:bg-green-900 px-2 py-1 rounded">
                            {(fieldConfidence * 100).toFixed(0)}%
                          </span>
                        )}
                      </div>
                    </div>
                  )}
                </div>
                {field.type === 'vendor' && field.key === 'vendor_id' && (
                  <div className="space-y-2">
                    <ReferenceDropdown
                      referenceType="vendor"
                      value={editedData.vendor_id}
                      displayLabel={editedData.vendor_name}
                      onChange={(id, label) => {
                        handleFieldChange('vendor_id', id);
                        handleFieldChange('vendor_name', label);
                      }}
                      placeholder="Search and select vendor..."
                      erpConnectionId={erpConnectionId}
                    />
                    <div>
                      <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">
                        Extracted Name
                      </label>
                      <input
                        type="text"
                        value={String(editedData.vendor_name_extracted || '')}
                        onChange={(e) => handleFieldChange('vendor_name_extracted', e.target.value)}
                        placeholder="Raw extracted vendor name"
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                  </div>
                )}

                {field.type === 'tax_ids' && (
                  <div className="space-y-3">
                    {taxIdRows.length > 0 ? (
                      taxIdRows.map((row) => (
                        <div key={row.id} className="grid grid-cols-1 gap-2 md:grid-cols-[minmax(0,1fr)_minmax(0,2fr)_auto]">
                          <SearchableSelect
                            value={row.key}
                            onChange={(selectedValue) => handleTaxIdRowChange(row.id, 'key', selectedValue)}
                            options={taxIdTypeOptions.map((option) => ({ id: option.value, label: option.label }))}
                            placeholder="Tax ID type"
                            clearable={false}
                          />
                          <input
                            type="text"
                            value={row.value}
                            onChange={(e) => handleTaxIdRowChange(row.id, 'value', e.target.value)}
                            placeholder="Tax ID value"
                            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-blue-500"
                          />
                          <Button
                            type="button"
                            variant="secondary"
                            onClick={() => handleRemoveTaxIdRow(row.id)}
                            disabled={isLoading}
                            className="justify-center"
                          >
                            Remove
                          </Button>
                        </div>
                      ))
                    ) : (
                      <p className="text-sm text-gray-500 dark:text-gray-400">
                        No tax IDs added.
                      </p>
                    )}
                    <div className="flex items-center justify-between gap-3">
                      <p className="text-xs text-gray-500 dark:text-gray-400">
                        Use GST/QST-style document tax IDs. Only rows with both a type and value are saved.
                      </p>
                      <Button
                        type="button"
                        variant="secondary"
                        onClick={handleAddTaxIdRow}
                        disabled={isLoading}
                      >
                        Add Tax ID
                      </Button>
                    </div>
                  </div>
                )}

                {field.type === "purchase_order" && (
                  <ReferenceDropdown
                    referenceType="purchase_order"
                    value={field.value}
                    displayLabel={field.value ? String(field.value) : undefined}
                    onChange={(_id, label) => {
                      handleFieldChange(field.key, label);
                    }}
                    placeholder="Search and select purchase order..."
                  />
                )}

                {field.type === 'number' && (
                  <ExpressionNumberInput
                    step="0.01"
                    value={field.value ?? ''}
                    onValueChange={(value) => handleFieldChange(field.key, value)}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                )}

                {field.type === 'date' && (
                  <input
                    type="date"
                    value={
                      field.value && typeof field.value === 'string'
                        ? field.value.split('T')[0]
                        : ''
                    }
                    onChange={(e) => handleFieldChange(field.key, e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                )}

                {field.type === 'text' && field.key !== 'vendor_name_extracted' && (
                  <input
                    type="text"
                    value={String(field.value || '')}
                    onChange={(e) => handleFieldChange(field.key, e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                )}

                {field.type === 'array' && (
                  <LineItemsEditor
                    items={Array.isArray(field.value) ? field.value : []}
                    onChange={handleLineItemsChange}
                    documentType={documentType}
                  />
                )}

                {field.type === 'tax_items' && (
                  <TaxItemsEditor
                    items={Array.isArray(field.value) ? field.value : []}
                    onChange={handleTaxItemsChange}
                    isLoading={isLoading}
                    taxIdTypes={taxIdTypes}
                  />
                )}
              </div>
            );
          })}
        </div>
      </div>
    );
  }
);

EditableDocumentFields.displayName = 'EditableDocumentFields';

export default EditableDocumentFields;
